import React from 'react';
import { constructMediaUrl } from '../../api/helpers';
import { MediaItem } from '../../api/structs/media';
import MediaThumbImage from '../clips/MediaThumbImage';
import { getMediaThumbUrl } from '../clips/sections/MediaEditorSection.helpers';
import { FrameBinding, FrameSlot } from '../clips/sections/referenceFrames';

export interface BoundFrame {
  slot: FrameSlot;
  label: string;
  /** What the video names for this slot. Null on a scene with no video. */
  binding: FrameBinding | null;
  /** The media item behind the binding, when one was reachable. */
  item?: MediaItem;
  /** These bytes moved since this dialog opened — a regeneration landed. */
  regenerated: boolean;
}

/**
 * The frames a scene's video is ACTUALLY bound to, as pictures.
 *
 * The Frames pane used to render nothing but a stack of prompt/provider forms:
 * two identical-looking forms, no images, and no way to tell which still each
 * one was about — the pane that exists to answer "which frames is this video
 * made from" never showed a frame. The thumbnails are read from the video's
 * bindings, so a frame swapped away from the clip's own still shows the frame
 * the generation will really open on.
 *
 * A resolved media item paints its cheap thumb derivative; a binding we could
 * not resolve still paints, straight from its URL, because a frame with no
 * media item of ours behind it is still a frame the operator needs to see.
 */
const SceneFrameThumbs: React.FC<{ frames: BoundFrame[] }> = ({ frames }) => {
  const shown = frames.filter((frame) => frame.item?.file_url || frame.binding?.url);
  if (shown.length === 0) return null;

  return (
    <div className="flex items-start gap-3" data-testid="scene-regen-bound-frames">
      {shown.map((frame) => {
        // The item's live file_url wins: it moves on every regeneration, while
        // the binding keeps naming the take the video was made from.
        const url = frame.item?.file_url || frame.binding?.url || '';
        return (
          <div key={frame.slot} className="flex-1 min-w-[8rem]" data-testid={`bound-frame-${frame.slot}`}>
            <div className="aspect-square w-full border border-white/10 bg-black/40 overflow-hidden">
              <MediaThumbImage
                key={url}
                thumbUrl={frame.item ? getMediaThumbUrl(frame.item) : ''}
                originalUrl={constructMediaUrl(url)}
                alt={frame.label}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
              />
            </div>
            <p className="mt-1.5 flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              {frame.label}
              {frame.regenerated && (
                <span
                  className="border border-amber-400/40 bg-amber-400/10 px-1 tracking-[0.12em] text-amber-200"
                  data-testid={`bound-frame-regenerated-${frame.slot}`}
                >
                  regenerated
                </span>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default SceneFrameThumbs;
